import React, { useEffect } from 'react';
import classNames from 'classnames';
import { BASE_URL } from '../../../constants/App';
import { CardProps } from '../../../Interfaces/CardProps';
import './Card.scss';
import CardMenu from './CardList';
import Button from '../../Atoms/Button/Button';
import { disableInteractiveElements, ternaryCheck } from '../../../Utils/WindowUtils';
import useStore from '../../../store/useStore';



/**
 * Card component
 * @param { [] | string } imageURL card image url
 * @param {string} targetUrl card anchor tag url
 * @param {string} title image titlex, level h2
 * @param {string} description image description, paragraph text
 * @param {string} imageLayout default 'regular'
 * @param {boolean} isFavourite true: if favourite or vice versa
 * @param {boolean} inStock true: if in stock or vice versa
 * @param {string} cartItemId cart item id
 * @param {function} onClickHandler function to handle click event
 * @returns Reusable card component
 */
export const Card: React.FC<CardProps> = props => {
  const {
    imageURL,
    redirectURL,
    title,
    description,
    imageLayout = 'regular',
    isFavourite=false,
    inStock = true,
    isCatering=false,
    price,
    dealNotAvailable = false,
    todayDeal = false,
    badgeTitle,
    badgeColor = null,
    productName,
    index,
    onClickHandler,
    MenuListTag = 'a',
    rewardType,
    enableButton,
    buttonText,
    isPlusIconLottieCircle,
    cardButtonClass,
    tosHandler,
    rewardText,
    onAnchorClick,
    heartIconClickHandler,
    buttonHandler,
    isDisabled,
    testAutoIds = {},
    children,
    showPlusIcon=false,
    plusIconHandler,
    showTickMark,
    bundlePrice,
    cardTextData,
    imageSize = '',
    cartItemId='', 
    comboBrightPrice,
    calories,
    cateringData = {},
    recommendedBadge,
  } = props;
  const hrefProperty = MenuListTag == 'a' ? { href: BASE_URL + redirectURL } : null;
  const {contextCardLoader, setContextCardLoader} = useStore();
  const isLottieLoading = ternaryCheck(
    contextCardLoader?.cardId,
    contextCardLoader?.cardId === cartItemId,
    false,
  );
  const cardClassName = classNames(`card__container card__container--${imageLayout}`, {
    'card__container--out-of-stock': inStock === false,
    'card__container--notAvailable': dealNotAvailable,
    'card__list--disabled': isLottieLoading,
    'card__categring_sub_menu': isCatering,
  });
  const cardMenuList = () => {
    return (
      <CardMenu
        imageURL={imageURL}
        title={title}
        description={description}
        isFavourite={isFavourite}
        inStock={inStock}
        isCatering={isCatering}
        price={price}
        badgeTitle={badgeTitle}
        badgeColor={badgeColor}
        rewardType={rewardType}
        isPlusIconLottieCircle={isPlusIconLottieCircle}
        rewardText={rewardText}
        enableButton={enableButton}
        buttonHandler={buttonHandler}
        buttonText={buttonText}
        tosHandler={tosHandler}
        productName={productName}
        heartIconClickHandler={heartIconClickHandler}
        isDisabled={isDisabled}
        cardButtonClassName={cardButtonClass}
        testAutoIds={testAutoIds}
        showPlusIcon={showPlusIcon}
        plusIconHandler={plusIconHandler}
        showTickMark={showTickMark}
        bundlePrice={bundlePrice}
        cardTextData={cardTextData}
        imageSize = {imageSize}
        isLottieLoading={isLottieLoading}
        comboBrightPrice={comboBrightPrice}
        calories={calories}
        todayDeal={todayDeal}
        cardDetailsCatering={cateringData}
        recommendedBadge={recommendedBadge}
      >
        {children}
      </CardMenu>
    );
  };

  const ariaLabel = ternaryCheck(
    isLottieLoading,
    (window as any).pleaseWaitValue,
    ternaryCheck(
      isFavourite,
      `My favorite ${productName} ${title}`,
      ternaryCheck(showPlusIcon, title, ''),
    ),
  );
  const dataTestAutoId = `${productName}`.replace(/\s+/g, '-').toLowerCase();
  const dataTitle = title?.replace(/\s+/g, '-').toLowerCase();
  const cardClassNames = classNames('card__list card__list--fullstretch');
  useEffect(() => {
    if (isLottieLoading) {
      if (!document.body.classList.contains('disableInteraction')) {
        disableInteractiveElements();
      }
    }
    return () => {
      if (document.body.classList.contains('disableInteraction')) {
        disableInteractiveElements();
        setContextCardLoader({
          cardId:'',
        });
      }
    };
  }, [isLottieLoading]);  

  return (
    <div className={cardClassName} >
      {!isFavourite ? (
        <MenuListTag
          data-testid={`card-list-${index}`}
          data-testauto-id={dataTitle}
          className="card__list"
          aria-label={ariaLabel}
          onClick={onAnchorClick}
          {...hrefProperty}
        >
          {cardMenuList()}
        </MenuListTag>
      ) : (
        <Button
          testAutoId={dataTestAutoId}
          onClick={onClickHandler}
          className={cardClassNames}
          aria-label={`${ariaLabel}`}
          tabIndex={0}>
          {cardMenuList()}
        </Button>
      )}
    </div>
  );
};
